import React, { useEffect, useState } from 'react';
import { UnifiedLayout } from './layouts/UnifiedLayout';
import { Login } from './pages/Login';
// Buyer Pages
import { Home } from './pages/Home';
import { Catalog } from './pages/Catalog';
import { Checkout } from './pages/Checkout';
import { Tracking } from './pages/Tracking';
import { OrderHistory } from './pages/OrderHistory';
import { BulkOrder } from './pages/BulkOrder';
import { AccountSettings } from './pages/AccountSettings';
import { OrderManagement } from './pages/OrderManagement';
// Admin Pages
import { Dashboard } from './pages/admin/Dashboard';
import { ProductManagement } from './pages/admin/ProductManagement';
import { DynamicPricing } from './pages/admin/DynamicPricing';
import { POConsolidation } from './pages/admin/POConsolidation';
import { Warehouse } from './pages/admin/Warehouse';
import { CooperativeList } from './pages/admin/CooperativeList';
import { LogisticsSettings } from './pages/admin/LogisticsSettings';
import { FinancialReport } from './pages/admin/FinancialReport';
import { OrderTracking } from './pages/admin/OrderTracking';
// Super Admin Pages
import { SuperAdminDashboard } from './pages/superadmin/Dashboard';
import { AgregatorManagement } from './pages/superadmin/AgregatorManagement';
import { RevenueAnalytics } from './pages/superadmin/RevenueAnalytics';
import { SystemHealth } from './pages/superadmin/SystemHealth';
// Logistics Pages
import { LogisticsDashboard } from './pages/logistics/Dashboard';
import { LogisticsShipments } from './pages/logistics/Shipments';
import { LogisticsTracking } from './pages/logistics/Tracking';
import { LogisticsPerformance } from './pages/logistics/Performance';
// Factory Pages
import { FactoryDashboard } from './pages/factory/Dashboard';
import { FactoryPurchaseOrders } from './pages/factory/PurchaseOrders';
import { FactoryProduction } from './pages/factory/Production';
import { FactoryInventory } from './pages/factory/Inventory';
// Warehouse Pages
import { WarehouseDashboard } from './pages/warehouse/Dashboard';
import { WarehouseInbound } from './pages/warehouse/Inbound';
import { WarehouseOutbound } from './pages/warehouse/Outbound';
import { WarehouseInventory } from './pages/warehouse/Inventory';
import { Documentation } from './pages/Documentation';
export function App() {
  const [currentPath, setCurrentPath] = useState(window.location.pathname || '/');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState<'buyer' | 'admin' | 'superadmin' | 'logistics' | 'factory' | 'warehouse' | null>(null);
  // Navigation helper
  const navigate = (path: string) => {
    window.history.pushState({}, '', path);
    setCurrentPath(path);
  };
  // Handle browser back/forward
  useEffect(() => {
    const handlePopState = () => setCurrentPath(window.location.pathname);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  // Handle login
  const handleLogin = (role: 'buyer' | 'admin' | 'superadmin' | 'logistics' | 'factory' | 'warehouse') => {
    setUserRole(role);
    setIsAuthenticated(true);
    // Navigate to appropriate dashboard
    if (role === 'buyer') {
      navigate('/');
    } else if (role === 'admin') {
      navigate('/admin/dashboard');
    } else if (role === 'superadmin') {
      navigate('/superadmin/dashboard');
    } else if (role === 'logistics') {
      navigate('/logistics/dashboard');
    } else if (role === 'factory') {
      navigate('/factory/dashboard');
    } else if (role === 'warehouse') {
      navigate('/warehouse/dashboard');
    }
  };
  // Handle logout
  const handleLogout = () => {
    setIsAuthenticated(false);
    setUserRole(null);
    navigate('/login');
  };
  // Show documentation page
  if (currentPath === '/documentation') {
    return <Documentation onNavigate={navigate} data-id="element-0" />;
  }
  // Show login page if not authenticated
  if (!isAuthenticated || !userRole) {
    return <Login onLogin={handleLogin} onNavigate={navigate} data-id="element-1" />;
  }
  // Determine role based on path
  const isLogistics = currentPath.startsWith('/logistics');
  const isFactory = currentPath.startsWith('/factory');
  const isWarehouse = currentPath.startsWith('/warehouse');
  const isSuperAdmin = currentPath.startsWith('/superadmin');
  const isAdmin = currentPath.startsWith('/admin');
  const role = isLogistics ? 'logistics' : isFactory ? 'factory' : isWarehouse ? 'warehouse' : isSuperAdmin ? 'superadmin' : isAdmin ? 'admin' : 'buyer';
  // ----------------------------------------------------------------------
  // UNIFIED ROUTING - All 6 Roles - ALL PAGES COMPLETE
  // ----------------------------------------------------------------------
  let content = <Home onNavigate={navigate} data-id="element-2" />;
  // Logistics Routes (4/4 Complete)
  if (currentPath === '/logistics' || currentPath === '/logistics/dashboard') {
    content = <LogisticsDashboard data-id="element-3" />;
  } else if (currentPath === '/logistics/shipments') {
    content = <LogisticsShipments data-id="element-4" />;
  } else if (currentPath === '/logistics/tracking') {
    content = <LogisticsTracking data-id="element-5" />;
  } else if (currentPath === '/logistics/performance') {
    content = <LogisticsPerformance data-id="element-6" />;
  }
  // Factory Routes (4/4 Complete)
  else if (currentPath === '/factory' || currentPath === '/factory/dashboard') {
    content = <FactoryDashboard data-id="element-7" />;
  } else if (currentPath === '/factory/purchase-orders') {
    content = <FactoryPurchaseOrders data-id="element-8" />;
  } else if (currentPath === '/factory/production') {
    content = <FactoryProduction data-id="element-9" />;
  } else if (currentPath === '/factory/inventory') {
    content = <FactoryInventory data-id="element-10" />;
  }
  // Warehouse Routes (4/4 Complete)
  else if (currentPath === '/warehouse' || currentPath === '/warehouse/dashboard') {
    content = <WarehouseDashboard data-id="element-11" />;
  } else if (currentPath === '/warehouse/inbound') {
    content = <WarehouseInbound data-id="element-12" />;
  } else if (currentPath === '/warehouse/outbound') {
    content = <WarehouseOutbound data-id="element-13" />;
  } else if (currentPath === '/warehouse/inventory') {
    content = <WarehouseInventory data-id="element-14" />;
  }
  // Super Admin Routes (4/4 Complete)
  else if (currentPath === '/superadmin' || currentPath === '/superadmin/dashboard') {
    content = <SuperAdminDashboard data-id="element-15" />;
  } else if (currentPath === '/superadmin/agregators') {
    content = <AgregatorManagement data-id="element-16" />;
  } else if (currentPath === '/superadmin/revenue') {
    content = <RevenueAnalytics data-id="element-17" />;
  } else if (currentPath === '/superadmin/system') {
    content = <SystemHealth data-id="element-18" />;
  }
  // Admin Routes (9/9 Complete)
  else if (currentPath === '/admin' || currentPath === '/admin/dashboard') {
    content = <Dashboard data-id="element-19" />;
  } else if (currentPath === '/admin/order-tracking') {
    content = <OrderTracking data-id="element-20" />;
  } else if (currentPath === '/admin/products') {
    content = <ProductManagement data-id="element-21" />;
  } else if (currentPath === '/admin/pricing') {
    content = <DynamicPricing data-id="element-22" />;
  } else if (currentPath === '/admin/po-consolidation') {
    content = <POConsolidation data-id="element-23" />;
  } else if (currentPath === '/admin/warehouse') {
    content = <Warehouse data-id="element-24" />;
  } else if (currentPath === '/admin/cooperatives') {
    content = <CooperativeList data-id="element-25" />;
  } else if (currentPath === '/admin/logistics') {
    content = <LogisticsSettings data-id="element-26" />;
  } else if (currentPath === '/admin/reports') {
    content = <FinancialReport data-id="element-27" />;
  }
  // Buyer Routes (8/8 Complete)
  else if (currentPath === '/' || currentPath === '/home') {
    content = <Home onNavigate={page => navigate(`/${page}`)} data-id="element-28" />;
  } else if (currentPath === '/catalog') {
    content = <Catalog onNavigate={page => navigate(`/${page}`)} data-id="element-29" />;
  } else if (currentPath === '/bulk-order') {
    content = <BulkOrder onNavigate={page => navigate(`/${page}`)} data-id="element-30" />;
  } else if (currentPath === '/order-management') {
    content = <OrderManagement onNavigate={page => navigate(`/${page}`)} data-id="element-31" />;
  } else if (currentPath === '/checkout') {
    content = <Checkout onNavigate={page => navigate(`/${page}`)} data-id="element-32" />;
  } else if (currentPath === '/tracking') {
    content = <Tracking onNavigate={page => navigate(`/${page}`)} data-id="element-33" />;
  } else if (currentPath === '/history') {
    content = <OrderHistory onNavigate={page => navigate(`/${page}`)} data-id="element-34" />;
  } else if (currentPath === '/account') {
    content = <AccountSettings onNavigate={page => navigate(`/${page}`)} data-id="element-35" />;
  }
  return <div className="min-h-screen bg-slate-50" data-id="element-36">
      <UnifiedLayout role={role} activeRoute={currentPath} onNavigate={navigate} onLogout={handleLogout} data-id="element-37">
        {content}
      </UnifiedLayout>
    </div>;
}